// src/hooks/useTpnCart.ts
import { useMemo } from 'react';
import { useAppSelector } from '../redux/hooks';
import { shouldShowCartIcon } from '../utils/tpnUtils';
import { checkIfRestaurantClosed, formatStartTimesToEST, isInBreakHours } from '../utils/reservationUtils';
import { PaymentPreferences } from '../types/restaurant';
import dayjs from 'dayjs';
import utc from 'dayjs/plugin/utc';
import timezone from 'dayjs/plugin/timezone';

dayjs.extend(utc);
dayjs.extend(timezone);

/**
- Custom hook for determining cart icon visibility based on TPN configuration
- 
- 
@param showCartIcon Whether cart icon is enabled in widget settings
- 
@returns boolean indicating if cart icon should be shown
 */
export const useTpnCart = (showCartIcon: boolean): boolean => {
  // Get TPN configuration from Redux store
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  // Get selected restaurant from Redux store
  const selectedRestaurant = useAppSelector((state) => state?.restaurant?.selectedRestaurant);
  
  // Get table availability state from Redux store
  const { 
    loading: tableLoading, 
    time_slots 
  } = useAppSelector((state) => state.tableAvailability);
  
  // Get site content for restaurant hours
  const siteContent = useAppSelector((state) => state.siteContent);
  
  // Memoize the cart visibility logic with complete reservation checks
  const shouldShowCart = useMemo(() => {
    // Check 0: isAddToCartEnabled (HIGHEST PRIORITY - from backend)
    let paymentPrefs: PaymentPreferences | null = null;
    if (selectedRestaurant?.payment_perferences) {
      try {
        paymentPrefs = typeof selectedRestaurant.payment_perferences === 'string'
          ? JSON.parse(selectedRestaurant.payment_perferences)
          : selectedRestaurant.payment_perferences;
      } catch (error) {
        // Failed to parse payment_perferences
      }
    }
    
    const isAddToCartEnabled = paymentPrefs?.isAddToCartEnabled ?? true;
    if (!isAddToCartEnabled) {
      return false;
    }
    
    // Check 1: Restaurant must be open today
    const todayDate = new Date().toISOString().split('T')[0];
    const isRestaurantClosed = checkIfRestaurantClosed(todayDate, siteContent);
    if (isRestaurantClosed) {
      return false;
    }
    
    // Check 1.1: Current EST time must be within today's operating hours from JSON
    const nowEST = dayjs().tz('America/New_York');
    const dayNames = ['sunday', 'monday', 'tuesday', 'wednesday', 'thursday', 'friday', 'saturday'];
    const currentDayName = dayNames[nowEST.day()];
    const hoursData = (siteContent?.data as any)?.reservation?.info?.hours?.[currentDayName];

    if (hoursData && !hoursData.is_closed) {
      let isWithinHours = false;

      // Check firstHalf shift (e.g., 17:00 to 21:00 on Tuesday)
      if (hoursData.firstHalf?.start && hoursData.firstHalf?.end) {
        const start1 = dayjs.tz(`${nowEST.format('YYYY-MM-DD')} ${hoursData.firstHalf.start}`, 'America/New_York');
        const end1 = dayjs.tz(`${nowEST.format('YYYY-MM-DD')} ${hoursData.firstHalf.end}`, 'America/New_York');
        if (nowEST.isAfter(start1) && nowEST.isBefore(end1)) {
          isWithinHours = true;
        }
      }

      // Check secondHalf shift if it exists (e.g., Friday/Weekend dinner shift)
      if (!isWithinHours && hoursData.secondHalf?.start && hoursData.secondHalf?.end) {
        const start2 = dayjs.tz(`${nowEST.format('YYYY-MM-DD')} ${hoursData.secondHalf.start}`, 'America/New_York');
        const end2 = dayjs.tz(`${nowEST.format('YYYY-MM-DD')} ${hoursData.secondHalf.end}`, 'America/New_York');
        if (nowEST.isAfter(start2) && nowEST.isBefore(end2)) {
          isWithinHours = true;
        }
      }

      if (!isWithinHours) {
        return false;
      }
    }

    // Check 1.5: Hide cart during break hours (between lunch and dinner)
    const inBreak = isInBreakHours(siteContent);
    if (inBreak) {
      return false;
    }
    
    // Check 2: TPN Configuration must exist
    const hasTpnConfig = shouldShowCartIcon(showCartIcon, tpnConfig);
    if (!hasTpnConfig) {
      return false;
    }
    
    // Check 4: Time slots must exist and have future availability
    if (!time_slots || time_slots.length === 0) {
      return false;
    }
    
    const filteredSlots = formatStartTimesToEST(time_slots, todayDate);
    if (filteredSlots.length === 0) {
      return false;
    }
    
    return true;
  }, [selectedRestaurant, siteContent, showCartIcon, tpnConfig, tableLoading, time_slots]);
  
  return shouldShowCart;
};

/**
- Custom hook for getting TPN configuration status
 */
export const useTpnStatus = () => {
  const tpnConfig = useAppSelector((state) => state?.tpnConfig?.data);
  
  return useMemo(() => {
    const status = {
      isActive: !!(tpnConfig?.tpn_config && tpnConfig.tpn_config.length > 0),
      configCount: tpnConfig?.tpn_config?.length || 0,
      hasConfig: !!tpnConfig,
      config: tpnConfig
    };
    
    return status;
  }, [tpnConfig]);
};